import { useLocale } from "../i18n/LocaleProvider";

// The two prose blocks in the home footer. They're here rather than in the
// string table because both weave inline links (and, for the disclaimer, a
// button) through the sentence — and where a link falls inside a sentence is a
// property of the language, not something a placeholder can carry.

/** Crawlable "about" text for parents and search engines. */
export function ParentsNote() {
  const { locale } = useLocale();

  if (locale === "fr") {
    return (
      <details className="parents-note">
        <summary>💛 Pour les parents — c'est quoi, Learn 2 Draw ?</summary>
        <div className="parents-note-body">
          <p>
            Learn 2 Draw est un{" "}
            <strong>
              site de dessin gratuit pour les jeunes enfants et les tout-petits
            </strong>{" "}
            (environ 2 à 8 ans) — sans publicité, sans inscription, sans cookies.
            Chaque dessin se trace tout seul à l'écran, étape par étape, et votre
            enfant le recopie sur une vraie feuille : l'écran enseigne, les
            crayons travaillent. Les tout-petits commencent par de{" "}
            <a href="/fr/shapes/">grandes formes simples</a> ; les plus grands
            dessinent des animaux à trois niveaux, des{" "}
            <a href="/flags/">drapeaux du monde</a> et des{" "}
            <a href="/paintings/">tableaux célèbres</a>. Les consignes sont lues à
            voix haute pour ceux qui ne lisent pas encore, et il existe un mode
            Lecture auto mains libres, une commande vocale et des coloriages à
            imprimer. Le site fonctionne hors connexion et s'installe sur l'écran
            d'accueil comme une application.
          </p>
          <p>
            Curieux de savoir ce que nous valons face aux autres ? Lisez notre
            comparatif honnête des{" "}
            <a href="/fr/best-drawing-apps-for-kids/">
              meilleures applications et sites de dessin pour les petits
            </a>{" "}
            — ou découvrez{" "}
            <a href="/why-drawing/">
              pourquoi le dessin fait tant de bien aux jeunes enfants
            </a>
            .
          </p>
        </div>
      </details>
    );
  }

  if (locale === "el") {
    return (
      <details className="parents-note">
        <summary>💛 Για τους γονείς — τι είναι το Learn 2 Draw;</summary>
        <div className="parents-note-body">
          <p>
            Το Learn 2 Draw είναι ένας{" "}
            <strong>
              δωρεάν ιστότοπος ζωγραφικής για μικρά παιδιά και νήπια
            </strong>{" "}
            (περίπου 2 έως 8 ετών) — χωρίς διαφημίσεις, χωρίς εγγραφή, χωρίς
            cookies. Κάθε σχέδιο ζωγραφίζεται μόνο του στην οθόνη, βήμα βήμα, και
            το παιδί σας το αντιγράφει σε αληθινό χαρτί: η οθόνη διδάσκει, οι
            μπογιές δουλεύουν. Τα νήπια ξεκινούν με{" "}
            <a href="/shapes/">μεγάλα απλά σχήματα</a>· τα μεγαλύτερα παιδιά
            ζωγραφίζουν ζώα σε τρία επίπεδα,{" "}
            <a href="/flags/">σημαίες του κόσμου</a> και{" "}
            <a href="/paintings/">διάσημους πίνακες</a>. Οι οδηγίες διαβάζονται
            φωναχτά για όσους δεν διαβάζουν ακόμα, και υπάρχει λειτουργία
            αυτόματης αναπαραγωγής χωρίς χέρια, φωνητικός έλεγχος και σελίδες για
            χρωματισμό προς εκτύπωση. Λειτουργεί χωρίς σύνδεση και εγκαθίσταται
            στην αρχική οθόνη σαν εφαρμογή.
          </p>
          <p>
            Αναρωτιέστε πώς συγκρινόμαστε με άλλες επιλογές; Διαβάστε τον
            ειλικρινή μας οδηγό για{" "}
            <a href="/best-drawing-apps-for-kids/">
              τις καλύτερες εφαρμογές και ιστότοπους ζωγραφικής για μικρά παιδιά
            </a>{" "}
            — ή δείτε{" "}
            <a href="/why-drawing/">
              γιατί η ζωγραφική κάνει τόσο καλό στα μικρά παιδιά
            </a>
            .
          </p>
        </div>
      </details>
    );
  }

  if (locale === "nl") {
    return (
      <details className="parents-note">
        <summary>💛 Voor ouders — wat is Learn 2 Draw?</summary>
        <div className="parents-note-body">
          <p>
            Learn 2 Draw is een{" "}
            <strong>
              gratis tekenwebsite voor kleine kinderen en peuters
            </strong>{" "}
            (ongeveer 2–8 jaar) — geen advertenties, geen aanmelding, geen cookies.
            Elke tekening tekent zichzelf stap voor stap op het scherm, en uw kind
            tekent mee op echt papier: het scherm leert, de potloden doen het werk.
            Peuters beginnen met{" "}
            <a href="/nl/vormen/">grote basisvormen</a>; oudere kinderen tekenen
            dieren op drie niveaus,{" "}
            <a href="/nl/vlaggen/">vlaggen van de wereld</a> en{" "}
            <a href="/nl/schilderijen/">beroemde schilderijen</a>. De instructies
            worden voorgelezen voor wie nog niet kan lezen, en er is een handsfree
            Auto Afspelen-modus, spraakbesturing en kleurplaten om af te drukken.
            Het werkt offline en installeert op het startscherm als een app.
          </p>
          <p>
            Benieuwd hoe we ons vergelijken met andere opties? Lees onze eerlijke
            gids voor{" "}
            <a href="/nl/beste-tekenapps-voor-kinderen/">
              de beste tekenapps &amp; -websites voor kleine kinderen
            </a>{" "}
            — of ontdek{" "}
            <a href="/nl/waarom-tekenen/">
              waarom tekenen zo goed is voor jonge kinderen
            </a>
            .
          </p>
        </div>
      </details>
    );
  }

  return (
    <details className="parents-note">
      <summary>💛 For parents — what is Learn 2 Draw?</summary>
      <div className="parents-note-body">
        <p>
          Learn 2 Draw is a{" "}
          <strong>free drawing website for little kids and toddlers</strong>{" "}
          (about ages 2–8) — no ads, no sign-up, no cookies. Every drawing draws
          itself on screen, step by step, and your child copies along on real
          paper: the screen teaches, the crayons do the work. Toddlers start with{" "}
          <a href="/shapes/">big basic shapes</a>; older kids draw animals at
          three levels, <a href="/flags/">flags of the world</a> and{" "}
          <a href="/paintings/">famous paintings</a>. Instructions are read aloud
          for pre-readers, and there's a hands-free Auto Play mode, voice control
          and printable coloring pages. It works offline and installs to a home
          screen like an app.
        </p>
        <p>
          Curious how we compare to other options? Read our honest guide to{" "}
          <a href="/best-drawing-apps-for-kids/">
            the best drawing apps &amp; websites for little kids
          </a>{" "}
          — or see{" "}
          <a href="/why-drawing/">why drawing is so good for young children</a>.
        </p>
      </div>
    </details>
  );
}

/** The gentle note about our simplified flags, with an inline "say hello" link. */
export function FlagsDisclaimer({ onSayHello }: { onSayHello: () => void }) {
  const { locale } = useLocale();

  if (locale === "fr") {
    return (
      <p className="footer-disclaimer">
        Un mot sur nos drapeaux : ils sont dessinés simplement, pour de petites
        mains, et ne sont donc pas toujours une représentation exacte du vrai
        drapeau — parfois pour des raisons techniques, et surtout pour rester
        faciles à dessiner par de jeunes enfants. Aucun manque de respect n'est
        jamais voulu. Si quelque chose vous semble faux ou vous heurte,{" "}
        <button className="footer-inline-link" onClick={onSayHello}>
          dites-nous bonjour
        </button>{" "}
        et faites-le nous savoir — ce ne sera pas intentionnel, et nous le
        corrigerons avec plaisir.
      </p>
    );
  }

  if (locale === "el") {
    return (
      <p className="footer-disclaimer">
        Μια κουβέντα για τις σημαίες μας: είναι σχεδιασμένες απλά, για μικρά
        χεράκια, γι' αυτό δεν είναι πάντα ακριβής απεικόνιση της αληθινής σημαίας
        — μερικές φορές για τεχνικούς λόγους, και κυρίως για να μένουν εύκολες
        στο σχεδίασμα από μικρά παιδιά. Ποτέ δεν υπάρχει πρόθεση ασέβειας. Αν
        κάτι σας φαίνεται λάθος ή σας ενοχλεί,{" "}
        <button className="footer-inline-link" onClick={onSayHello}>
          πείτε μας γεια
        </button>{" "}
        και ενημερώστε μας — δεν θα ήταν σκόπιμο, και θα το διορθώσουμε με χαρά.
      </p>
    );
  }

  if (locale === "nl") {
    return (
      <p className="footer-disclaimer">
        Een opmerking over onze vlaggen: ze zijn eenvoudig getekend voor kleine
        handjes, dus ze zijn niet altijd een exacte weergave van de echte vlag —
        soms om technische redenen, en vooral om ze makkelijk te houden voor
        jonge kinderen om te tekenen. Er is nooit gebrek aan respect bedoeld.
        Als er iets verkeerd lijkt of u stoort,{" "}
        <button className="footer-inline-link" onClick={onSayHello}>
          zeg hallo
        </button>{" "}
        en laat het ons weten — het zou niet opzettelijk zijn geweest, en we
        lossen het graag op.
      </p>
    );
  }

  return (
    <p className="footer-disclaimer">
      A note on our flags: they're drawn simply for little hands, so they aren't
      always an exact representation of the real flag — sometimes for technical
      reasons, and mostly to keep them easy for young children to draw. No
      disrespect is ever intended. If anything looks wrong or upsets you, please{" "}
      <button className="footer-inline-link" onClick={onSayHello}>
        say hello
      </button>{" "}
      and let us know — it would not have been intentional, and we'll gladly fix
      it.
    </p>
  );
}
